import React from 'react';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import Button from '../Button';
import CloseButton from '../Button/CloseButton';
import { Props as IconProps, IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SystemNotificationIcon from './SystemNotificationIcon';
import { ReverseTheme } from '@wix/da-react-context/pkg/ThemeContext';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import s from './SystemNotification.scss';

export interface Props {
  iconComponent?: React.ComponentType<IconProps>;
  rootRef?: React.RefObject<HTMLElement | null>;
  customIcon?: React.ReactNode;
  heading?: React.ReactNode;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
  backgroundDeviation?: PapiDeviation;
  multiLine?: boolean;
  limitTextWidth?: boolean;
  actions?: (
    | {
        type: 'positive' | 'negative';
        label: string;
        href?: string;
        biData?: BiEvent;
        closeOnClick?: boolean;
        onClick?: () => void;
      }
    | false
    | null
    | undefined
  )[];
  dataHook?: string;
  withShadow?: boolean;
  withCloseButton?: boolean;
  closeButtonClassName?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClose: () => void;
}

// CR44
const SystemNotification: React.FC<React.PropsWithChildren<Props>> = ({
  iconComponent,
  rootRef,
  customIcon,
  heading,
  className,
  textClassName,
  closeButtonClassName,
  iconClassName,
  backgroundDeviation,
  children,
  onClose,
  actions = [],
  multiLine,
  onMouseEnter,
  onMouseLeave,
  dataHook,
  withShadow = true,
  withCloseButton = true,
  limitTextWidth = true,
}) => {
  const filteredActions = actions.filter(a => !!a);
  const icon = customIcon ? (
    customIcon
  ) : iconComponent ? (
    <SystemNotificationIcon
      iconComponent={iconComponent}
      backgroundDeviation={backgroundDeviation}
    />
  ) : null;

  return (
    <ReverseTheme>
      <div
        ref={rootRef as any}
        className={classnames(
          className,
          s['root'] as any,
          withShadow && s['shadow'],
          icon && s['with-icon'],
          limitTextWidth && s['limit-text-width']
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-hook={dataHook}
      >
        {icon && (
          <div className={classnames(iconClassName, s['icon'])}>{icon}</div>
        )}
        <div className={s['text-wrap']}>
          <div
            className={classnames(
              s['text'],
              multiLine && s['multiline'],
              textClassName
            )}
          >
            {children}
          </div>
        </div>
        {filteredActions.length > 0 && (
          <div className={s['actions']}>
            {filteredActions.map(
              action =>
                action && (
                  <Button
                    key={action.label}
                    className={s['action']}
                    view={action.type === 'positive' ? 'b3' : 'b31'}
                    href={action.href}
                    onClick={
                      action.closeOnClick
                        ? () => {
                            action.onClick && action.onClick();
                            onClose();
                          }
                        : action.onClick
                    }
                    biData={action.biData}
                  >
                    {action.label}
                  </Button>
                )
            )}
          </div>
        )}
        {withCloseButton && (
          <div className={s['close-button-wrap']}>
            <CloseButton
              view="b5-2"
              iconSize={IconSize.NANO}
              className={classnames(s['close-button'], closeButtonClassName)}
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        )}
      </div>
    </ReverseTheme>
  );
};
SystemNotification.displayName = 'SystemNotification';

export default SystemNotification;
